import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Mail, Phone } from 'lucide-react'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import useIsMobile from 'hooks/useIsMobile'
import { mailtoHref, telHref } from 'utils/contact'

export interface AssigneePopoverProps {
	/** The assignee as the task populates it: name, firstName, email, phone, image. */
	user: any
	/** The clicked avatar's rect, which the card anchors to. */
	anchor: DOMRect
	onClose: () => void
}

const WIDTH = 224
const GAP = 8

/**
 * Who a task is assigned to, and how to reach them (MYD-534).
 *
 * One component for the four task screens (Tasks, Project, Location and
 * Portfolio tasks), each of which had its own copy that showed the email as
 * grey text with no way to act on it.
 *
 * The email is a `mailto:` link everywhere. The phone appears only on mobile,
 * as tap-to-call: on a desktop a `tel:` link usually opens nothing, which reads
 * as a broken action. Same rule as the partner profile popup (MYD-533).
 *
 * Anchored under the clicked avatar, and flipped above it when there is no room
 * below, so a row near the bottom of the screen does not open a card that runs
 * off it. Closes on an outside click and on Escape.
 */
const AssigneePopover = ({ user, anchor, onClose }: AssigneePopoverProps) => {
	const isMobile = useIsMobile()
	const ref = useRef<HTMLDivElement>(null)
	const [top, setTop] = useState(anchor.bottom + GAP)

	useLayoutEffect(() => {
		const height = ref.current?.offsetHeight ?? 0
		const fitsBelow = anchor.bottom + GAP + height <= window.innerHeight - GAP
		setTop(fitsBelow ? anchor.bottom + GAP : Math.max(GAP, anchor.top - GAP - height))
	}, [anchor])

	useEffect(() => {
		const onPointerDown = (e: MouseEvent) => {
			if (ref.current && !ref.current.contains(e.target as Node)) onClose()
		}
		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === 'Escape') onClose()
		}
		document.addEventListener('mousedown', onPointerDown)
		document.addEventListener('keydown', onKeyDown)
		return () => {
			document.removeEventListener('mousedown', onPointerDown)
			document.removeEventListener('keydown', onKeyDown)
		}
	}, [onClose])

	const name = user?.name || user?.firstName || 'Unassigned'
	const mailto = mailtoHref(user?.email)
	const tel = isMobile ? telHref(user?.phone) : null

	return (
		<div
			ref={ref}
			role="dialog"
			aria-label={`Assigned to ${name}`}
			className="fixed z-50 bg-white rounded-xl shadow-lg border border-gray-200 p-4"
			style={{
				top,
				width: WIDTH,
				left: Math.max(GAP, Math.min(anchor.left, window.innerWidth - WIDTH - GAP)),
			}}
		>
			<div className="flex flex-col items-center text-center">
				<Avatar className="w-16 h-16 mb-2">
					<AvatarImage src={user?.image} />
					<AvatarFallback className="text-xl">{name.charAt(0).toUpperCase()}</AvatarFallback>
				</Avatar>
				<h3 className="text-sm font-medium text-gray-900 mb-1">{name}</h3>
				{mailto ? (
					<a
						href={mailto}
						title={user.email}
						className="inline-flex items-center gap-1.5 max-w-full text-xs text-[#0080fe] hover:underline"
					>
						<Mail className="w-3.5 h-3.5 shrink-0" />
						<span className="truncate">{user.email}</span>
					</a>
				) : (
					user?.email && <p className="text-xs text-gray-400 truncate max-w-full">{user.email}</p>
				)}
				{tel && (
					<a href={tel} className="inline-flex items-center gap-1.5 mt-1 text-xs text-[#0080fe] hover:underline">
						<Phone className="w-3.5 h-3.5 shrink-0" />
						{user.phone}
					</a>
				)}
			</div>
		</div>
	)
}

export default AssigneePopover
